import api from "./api";
import { downloadBlob } from "@/utils/helpers";

export interface DocumentMetadata {
  id: string;
  filename: string;
  file_type: string; 
  file_size: number;
  checksum_md5: string;
  content_type: number;
  status: string;
  object_id: string;
  uploaded_by: string;
  created_at: string;
  updated_at: string;
}

// Browsers can only render these types in a new tab. Anything else (docx, xlsx, zip, ...)
// silently triggers the browser's own auto-download with a generated name, since a blob: URL
// carries no filename metadata — so those types must go through a named download instead.
const isBrowserRenderable = (contentType: string): boolean =>
  contentType.startsWith('image/') ||
  contentType === 'application/pdf' ||
  contentType.startsWith('text/') ||
  contentType.startsWith('video/') ||
  contentType.startsWith('audio/');

const getFilenameFromContentDisposition = (contentDisposition: string | undefined): string | null => {
  if (!contentDisposition) return null;
  const filenameMatch =
    contentDisposition.match(/filename\*=(?:UTF-8'')?(.+?)(?:;|$)/i) ||
    contentDisposition.match(/filename="?([^";\n]+)"?/i);
  return filenameMatch && filenameMatch[1] ? decodeURIComponent(filenameMatch[1]) : null;
};

export const downloadDocument = async (documentId: string) => {
    try {
      const response = await api.get(
        `documents/documents/${documentId}/download/`,
        {
          responseType: 'blob',
          maxRedirects: 5,
        }
      );

      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      const filename = getFilenameFromContentDisposition(response.headers['content-disposition']) || 'document.pdf';

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Download failed:', error);
      throw error;
    }
  };

export const viewDocumentInline = async (documentId: string) => {
  try {
    const response = await api.get(
      `documents/documents/${documentId}/inline/`,
      {
        responseType: 'blob',
        maxRedirects: 5,
      }
    );

    const blob = response.data;

    const contentType =
      response.headers['content-type'] || 'application/pdf';

    const fileBlob = new Blob([blob], { type: contentType });
    const url = window.URL.createObjectURL(fileBlob);

    if (isBrowserRenderable(contentType)) {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      const filename = getFilenameFromContentDisposition(response.headers['content-disposition']) || 'document';
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }

    setTimeout(() => {
      window.URL.revokeObjectURL(url);
    }, 60_000);

  } catch (error) {
    console.error('View document failed:', error);
    throw error;
  }
};

export const getDocumentUrl = async (documentId: string): Promise<string> => {
  const response = await api.get(
    `documents/documents/${documentId}/inline/`,
    { responseType: 'blob' }
  );
  
  return URL.createObjectURL(response.data);
};

export const getDocumentUrls = async (documentIds: string[]) => {
  return Promise.all(
    documentIds.map(id => getDocumentUrl(id))
  );
};

export const handleDownload = (file: DocumentMetadata) => {
    if(file.id) downloadDocument(file.id);
  };

export const handlePreview = (file: DocumentMetadata) => {
  if (file.id) viewDocumentInline(file.id);
};

export const downloadFromUrl = async (url: string): Promise<void> => {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Network response was not ok: ${response.status}`);

    const blob = await response.blob();

    // Try to determine filename from headers or URL
    let filename: string | null = null;
    const contentDisposition = response.headers.get('content-disposition');
    if (contentDisposition) {
      const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/.exec(contentDisposition);
      if (match && match[1]) filename = decodeURIComponent(match[1]);
    }

    if (!filename) {
      try {
        const u = new URL(url);
        const rscd = u.searchParams.get('rscd');
        if (rscd) {
          const fnMatch = /filename%3D%22([^%22]+)%22/i.exec(rscd) || /filename=\"?([^\";]+)\"?/i.exec(decodeURIComponent(rscd));
          if (fnMatch && fnMatch[1]) filename = decodeURIComponent(fnMatch[1]);
        }
        if (!filename) {
          const pathParts = u.pathname.split('/');
          filename = pathParts[pathParts.length - 1] || 'download';
        }
      } catch (e) {
        filename = 'download';
      }
    }

    downloadBlob(blob, filename || 'download');
  };
